import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Headphones, X } from 'lucide-react';

interface AudioModeReminderBubbleProps {
  targetRef: React.RefObject<HTMLElement>;
  onDismiss: () => void;
  onTurnOff: () => void;
  eyebrow: string;
  message: string;
  ctaLabel: string;
  dismissLabel: string;
  autoCloseMs?: number;
}

const HALO_PADDING = 8;
const BUBBLE_WIDTH = 308;
const BUBBLE_EST_HEIGHT = 138;
const VIEWPORT_MARGIN = 14;

export function AudioModeReminderBubble({
  targetRef,
  onDismiss,
  onTurnOff,
  eyebrow,
  message,
  ctaLabel,
  dismissLabel,
  autoCloseMs = 10000,
}: AudioModeReminderBubbleProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [visible, setVisible] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const nextRect = targetRef.current?.getBoundingClientRect();
      if (!nextRect || nextRect.width <= 0 || nextRect.height <= 0) {
        setRect(null);
        return;
      }
      setRect(nextRect);
    };

    measure();
    const timers = [80, 220, 480].map((delay) => window.setTimeout(measure, delay));
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [targetRef]);

  useEffect(() => {
    const raf = window.requestAnimationFrame(() => setVisible(true));
    const closeTimer = window.setTimeout(() => {
      setVisible(false);
      window.setTimeout(onDismiss, 240);
    }, autoCloseMs);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(closeTimer);
    };
  }, [autoCloseMs, onDismiss]);

  if (!rect) return null;

  const centerX = rect.left + rect.width / 2;
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const placeBelow = spaceBelow >= BUBBLE_EST_HEIGHT || spaceBelow >= spaceAbove;
  const bubbleLeft = Math.max(
    VIEWPORT_MARGIN,
    Math.min(centerX - BUBBLE_WIDTH / 2, window.innerWidth - BUBBLE_WIDTH - VIEWPORT_MARGIN),
  );
  const arrowLeft = Math.max(18, Math.min(centerX - bubbleLeft, BUBBLE_WIDTH - 18));
  const halo = {
    left: rect.left - HALO_PADDING,
    top: rect.top - HALO_PADDING,
    width: rect.width + HALO_PADDING * 2,
    height: rect.height + HALO_PADDING * 2,
  };

  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed z-[999] rounded-full"
        style={{
          ...halo,
          opacity: visible ? 1 : 0,
          transition: 'opacity 280ms ease',
          boxShadow:
            '0 0 0 2px rgba(37,99,235,0.68), 0 0 0 10px rgba(37,99,235,0.14), 0 0 30px rgba(37,99,235,0.34)',
          animation: visible ? 'sl-audio-reminder-pulse 2.4s ease-in-out infinite' : 'none',
        }}
      />

      <div
        role="status"
        aria-live="polite"
        className="fixed z-[1000]"
        style={{
          left: bubbleLeft,
          width: BUBBLE_WIDTH,
          top: placeBelow ? rect.bottom + 14 : undefined,
          bottom: placeBelow ? undefined : window.innerHeight - rect.top + 14,
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : `translateY(${placeBelow ? '-4px' : '4px'})`,
          transition: 'opacity 320ms ease, transform 320ms ease',
        }}
      >
        <div
          aria-hidden="true"
          className="absolute"
          style={{
            [placeBelow ? 'top' : 'bottom']: -5,
            left: arrowLeft,
            width: 12,
            height: 12,
            background: 'white',
            transform: 'translateX(-50%) rotate(45deg)',
            borderTop: placeBelow ? '1px solid rgba(37,99,235,0.2)' : 'none',
            borderLeft: placeBelow ? '1px solid rgba(37,99,235,0.2)' : 'none',
            borderBottom: !placeBelow ? '1px solid rgba(37,99,235,0.2)' : 'none',
            borderRight: !placeBelow ? '1px solid rgba(37,99,235,0.2)' : 'none',
          }}
        />
        <div className="relative rounded-xl bg-white p-3.5 shadow-[0_18px_44px_-12px_rgba(15,23,42,0.38),0_0_0_1px_rgba(37,99,235,0.18)]">
          <button
            type="button"
            onClick={onDismiss}
            aria-label={dismissLabel}
            className="absolute right-2 top-2 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>

          <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-600">
            <Headphones className="h-3 w-3" />
            {eyebrow}
          </div>
          <p className="mb-3 pr-4 text-[13px] leading-relaxed text-gray-800">
            {message}
          </p>
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => {
                onTurnOff();
                onDismiss();
              }}
              className="rounded-md bg-gray-900 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm transition hover:bg-gray-800"
            >
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes sl-audio-reminder-pulse {
          0%, 100% { box-shadow: 0 0 0 2px rgba(37,99,235,0.68), 0 0 0 10px rgba(37,99,235,0.14), 0 0 30px rgba(37,99,235,0.34); }
          50% { box-shadow: 0 0 0 2px rgba(37,99,235,0.88), 0 0 0 16px rgba(37,99,235,0.08), 0 0 40px rgba(37,99,235,0.44); }
        }
      `}</style>
    </>
  );
}

export default AudioModeReminderBubble;
